/**
 * The audit's Exceptions tab — what a person has to decide on, and the record
 * of what they decided. Figures first, then the list; each row opens a panel
 * on the right with the decisions.
 */

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import {
	useAuditExceptionsQuery,
	useExceptionQuery,
} from '../../queries/exceptions';
import {
	useAnalysisCatalogueQuery,
	useAnalysisOverviewQuery,
} from '../../queries/analysis';
import { useAuth } from '../../provider/auth';
import { PageHeader } from '../../provider/pageTitle';
import { StatusBadge } from '../../shared/ui';
import { count, date, humanise, moneyShort } from '../../utils/format';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
	type FilterSpec,
} from '../shared/tables';
import { EmptyState, ErrorState, LoadingState } from '../shared/states';
import { Figure, Figures, SeverityBadge } from '../shared/badges';
import { useAuditContext } from '../audits/AuditWorkspace';
import { ExceptionDetailsDrawer } from './ExceptionDetailsDrawer';
import type { AuditException, ExceptionStatus } from '../../schemas';

const STATUS_ORDER: ExceptionStatus[] = [
	'OPEN',
	'ASSIGNED',
	'IN_REVIEW',
	'ACCEPTED',
	'CORRECTED',
	'ESCALATED',
	'CLOSED',
];

export default function ExceptionsScreen() {
	const { audit, refresh, crumbs } = useAuditContext();
	const { vocabulary } = useAuth();
	const { exceptionId: routeExceptionId } = useParams();
	const [filters, setFilters] = useState<{
		status: string;
		severity: string;
		analysis: string;
		q: string;
	}>({
		status: '',
		severity: '',
		analysis: '',
		q: '',
	});
	const analyses = useAnalysisOverviewQuery(audit.pkAuditId);
	/* The catalogue too: an exception can outlive its analysis's place in
     this audit's selection, and must still read as "Ref Name", never a code
     name nobody chose. */
	const catalogue = useAnalysisCatalogueQuery(audit.module);
	const analysisName = (slug: string) => {
		const row = analyses.data?.rows.find((item) => item.slug === slug);
		if (row) return `${row.ref} ${row.name}`;
		const definition = catalogue.data?.find((item) => item.slug === slug);
		return definition ? `${definition.code} ${definition.name}` : '';
	};
	const fullScreen = useFullScreen('Exceptions');
	/* Seeded once, from the URL — a reader arriving on the deep link opens
     straight to the exception it names. A row click afterwards just replaces
     this with local state; see the file header for why this never navigates. */
	const [openId, setOpenId] = useState<string | null>(
		routeExceptionId ?? null,
	);

	const exceptions = useAuditExceptionsQuery(audit.pkAuditId);
	const all = exceptions.data ?? [];
	const fromList =
		all.find((item) => item.pkAuditExceptionId === openId) ?? null;

	/* The one gap `fromList` cannot cover: a deep link opened before the
     audit-scoped list has finished its first fetch. Waits for that first
     fetch to settle so a `openId` that WILL show up in `all` a moment later
     never fires a second, redundant request for the same row. */
	const single = useExceptionQuery(
		openId,
		!fromList && !exceptions.isPending,
	);
	/* `?? null`, because React Query reports "nothing yet" as `undefined` where
     the hook this replaces used `null`, and the drawer's prop is `| null`. */
	const openException = fromList ?? single.data ?? null;

	const rows = useMemo(
		() =>
			all.filter((item) => {
				if (filters.status && item.status !== filters.status)
					return false;
				if (filters.severity && item.severity !== filters.severity)
					return false;
				if (filters.analysis && item.analysisSlug !== filters.analysis)
					return false;
				if (
					filters.q &&
					!`${item.title} ${item.detail}`
						.toLowerCase()
						.includes(filters.q.toLowerCase())
				)
					return false;
				return true;
			}),
		[all, filters],
	);

	const specs: FilterSpec[] = [
		{
			key: 'status',
			label: 'Status',
			kind: 'select',
			options: [
				{ value: '', label: 'Any status' },
				...STATUS_ORDER.map((value) => ({
					value,
					label:
						vocabulary?.exceptionStatus?.[value]?.label ??
						humanise(value),
				})),
			],
		},
		{
			key: 'severity',
			label: 'Severity',
			kind: 'select',
			options: [
				{ value: '', label: 'Any severity' },
				...['BLOCKING', 'EXCEPTION', 'WARNING', 'INFO'].map(
					(value) => ({
						value,
						label:
							vocabulary?.severity?.[value]?.label ??
							humanise(value),
					}),
				),
			],
		},
		{
			key: 'analysis',
			label: 'Analysis',
			kind: 'select',
			options: [
				{ value: '', label: 'Any analysis' },
				...Array.from(
					new Set(all.map((item) => item.analysisSlug)),
				).map((slug) => ({
					value: slug,
					label:
						analysisName(slug) ||
						'An analysis no longer in the catalogue',
				})),
			],
		},
		{
			key: 'q',
			label: 'Search',
			kind: 'search',
			placeholder: 'What was found',
		},
	];

	const columns: Array<Column<AuditException>> = [
		{
			key: 'title',
			label: 'Exception',
			width: 340,
			className: 'stack2',
			sortValue: (item) => item.title,
			render: (item) => (
				<>
					<b>{item.title}</b>
					<div className="l2">{item.detail}</div>
				</>
			),
		},
		{
			key: 'analysis',
			label: 'Analysis',
			width: 220,
			sortValue: (item) => analysisName(item.analysisSlug),
			render: (item) => (
				<span className="small">{analysisName(item.analysisSlug)}</span>
			),
		},
		{
			key: 'severity',
			label: 'Severity',
			width: 100,
			sortValue: (item) =>
				({ BLOCKING: 0, EXCEPTION: 1, WARNING: 2, INFO: 3 })[
					item.severity
				] ?? 9,
			render: (item) => <SeverityBadge value={item.severity} />,
		},
		{
			key: 'rows',
			label: 'Rows',
			num: true,
			width: 110,
			sortValue: (item) => item.rowsAffected,
			render: (item) => count(item.rowsAffected),
		},
		{
			key: 'value',
			label: 'Value',
			num: true,
			width: 120,
			sortValue: (item) => item.valueAffected ?? -1,
			render: (item) =>
				item.valueAffected === null ? (
					<span className="mut">—</span>
				) : (
					<span
						title={`₹${item.valueAffected.toLocaleString('en-IN')}`}
					>
						{moneyShort(item.valueAffected)}
					</span>
				),
		},
		{
			key: 'status',
			label: 'Status',
			width: 130,
			sortValue: (item) => item.status,
			render: (item) => (
				<StatusBadge group="exceptionStatus" value={item.status} />
			),
		},
		{
			key: 'created',
			label: 'Raised',
			width: 120,
			sortValue: (item) => item.createdAt,
			render: (item) => date(item.createdAt),
		},
	];

	const openCount = all.filter((item) => item.status === 'OPEN').length;
	const blockingOpen = all.filter(
		(item) => item.status === 'OPEN' && item.isBlocking,
	).length;
	const resolved = all.length - openCount;
	const openValued = all.filter(
		(item) => item.status === 'OPEN' && item.valueAffected !== null,
	);
	const openValue = openValued.reduce(
		(sum, item) => sum + (item.valueAffected ?? 0),
		0,
	);
	// Used by the Value at stake note, which is commented out below for now.
	// const openUnvalued =
	//   all.filter((item) => item.status === 'OPEN').length - openValued.length

	return (
		<div className="stack">
			<PageHeader
				breadcrumbs={crumbs}
				title="Exceptions"
				subtitle={`${count(all.length)} raised · ${count(openCount)} open`}
			/>

			<Figures>
				<Figure
					label="Blocking, open"
					value={count(blockingOpen)}
					tone={blockingOpen > 0 ? 'bad' : 'accent'}
					// meta="Stops the report being issued"
				/>
				<Figure label="Open" value={count(openCount)} tone="warn" />
				<Figure label="Decided" value={count(resolved)} tone="ok" />
				<Figure
					label="Value at stake (open)"
					tone="info"
					value={
						<span title={`₹${openValue.toLocaleString('en-IN')}`}>
							{moneyShort(openValue)}
						</span>
					}
					// meta={
					//   openUnvalued > 0 ? `${count(openUnvalued)} open with no value` : undefined
					// }
				/>
			</Figures>

			<FilterBar
				specs={specs}
				values={filters}
				onChange={(key, value) =>
					setFilters((current) => ({ ...current, [key]: value }))
				}
				onClear={() =>
					setFilters({
						status: '',
						severity: '',
						analysis: '',
						q: '',
					})
				}
				resultNote={`${count(rows.length)} of ${count(all.length)} exceptions`}
				actions={fullScreen.button}
			/>

			<ErrorState error={exceptions.error} onRetry={exceptions.refetch} />

			{exceptions.isPending ? (
				<LoadingState label="Loading exceptions…" />
			) : all.length === 0 ? (
				<EmptyState title="No exceptions">
					{(analyses.data?.summary.completed ?? 0) > 0
						? 'The analyses found nothing that needs a decision.'
						: 'Exceptions are raised when analyses find something. Run the analyses first.'}
				</EmptyState>
			) : (
				/* The card alone, no padding: `Card` pads its body, which left a
           border of white round the table. */
				<Box className="card">
					<div className="card-bd flush">
						<DataTable
							rows={rows}
							columns={columns}
							rowKey={(item) => item.pkAuditExceptionId}
							onRowClick={(item) =>
								setOpenId(item.pkAuditExceptionId)
							}
							caption="Exceptions"
							fullScreen={fullScreen}
							maxHeight="main"
							empty={
								<p className="mut small">
									No exception matches those filters.
								</p>
							}
						/>
					</div>
				</Box>
			)}

			<ExceptionDetailsDrawer
				exception={openException}
				onClose={() => setOpenId(null)}
				onChanged={() => {
					/* Refetching the list is enough even for an exception opened via
             the single-fetch fallback above: once the list resolves,
             `fromList` finds the same row and `single`'s own result stops
             being read at all. */
					void exceptions.refetch();
					/* Not a second fetch of our own — see `AuditWorkspace.useAuditContext`.
             The workspace bar's open-exceptions pill and the report's own
             readiness checklist both read this same count. */
					void refresh();
				}}
			/>
		</div>
	);
}
